import { DatePipe, JsonPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, inject, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { PaginationComponent } from 'src/app/components/pagination/pagination.component';
import { AddInventoryAdjustment, GetCatalogResourceInventory, GetInventoryMovement, GetInventoryReservationTTL } from 'src/app/models/inventory/inventory';
import { Paged } from 'src/app/models/shared/paged';
import { InventoryService } from 'src/app/services/inventory.service';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import Swal from 'sweetalert2';


@Component({
  selector: 'app-modal-inventory-management',
  templateUrl: './modal-inventory-management.component.html',
  styleUrls: ['./modal-inventory-management.component.css'],
  imports: [NgClass, FormsModule, ReactiveFormsModule, NgFor, DatePipe, PaginationComponent,
    NgIf
  ]
})
export class ModalInventoryManagementComponent {
  changeData = output<void>();

  private inventorySvc = inject(InventoryService);

  header: any;
  kpis: any;
  config: any;

  movements = [] as GetInventoryMovement[];

  movementsLoading = signal(false);
  movementsPage = signal(1);
  movementsPageSize = signal(10);
  movementsTotalItems = signal(0);

  private fb = inject(FormBuilder);

  adjustmentForm = this.fb.group({
    movementTypeId: [1, Validators.required],
    quantity: [null, [Validators.required, Validators.min(1)]],
    reason: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(250)]]
  });

  savingAdjustment = signal(false);
  reservations: GetInventoryReservationTTL[] = [];
  reservationsLoading = signal(false);

  reservationsPage = signal(1);
  reservationsPageSize = signal(10);
  reservationsTotalItems = signal(0);

  reservationsFilterKey: string | null = null;
  reservationsFilterValue: string | null = null;


  open(item: GetCatalogResourceInventory) {
    this.header = item;

    this.adjustmentForm.reset({
      movementTypeId: 1,
      quantity: null,
      reason: ''
    });

    this.movementsPage.set(1);
    this.loadKpis();
    this.loadMovements();
    this.loadReservationsTTL();
    this.loadConfig();

    const modal = new (window as any).bootstrap.Modal('#modalInventoryManagement', {});
    modal.show();
  }


  loadKpis() {
    this.inventorySvc.getInventoryKpis(this.header.versionId)
      .subscribe(res => this.kpis = res);
  }

  loadConfig() {
    this.inventorySvc.getInventoryConfiguration(this.header.versionId)
      .subscribe(res => this.config = res);
  }

  loadMovements() {
    this.movementsLoading.set(true);

    this.inventorySvc.getInventoryMovements(
      this.header.versionId,
      {
        page: this.movementsPage(),
        pageSize: this.movementsPageSize()
      })
      .pipe(finalize(() => this.movementsLoading.set(false)))
      .subscribe({
        next: (res: Paged<GetInventoryMovement>) => {
          this.movements = res.items || [];
          this.movementsTotalItems.set(res.totalItems || 0);
        },
        error: () => {
          this.movements = [];
          this.movementsTotalItems.set(0);
        }
      });
  }

  onMovementsPageChange(page: number) {
    this.movementsPage.set(page);
    this.loadMovements();
  }

  onMovementsPageSizeChange(size: number) {
    this.movementsPageSize.set(size);
    this.movementsPage.set(1);
    this.loadMovements();
  }


  loadReservationsTTL() {
    this.reservationsLoading.set(true);

    this.inventorySvc.getActiveReservationsTTL(this.header.versionId, {
      page: this.reservationsPage(),
      pageSize: this.reservationsPageSize(),
      filterKey: this.reservationsFilterKey || undefined,
      filterValue: this.reservationsFilterValue || undefined
    })
      .pipe(finalize(() => this.reservationsLoading.set(false)))
      .subscribe({
        next: (res) => {
          this.reservations = res.items || [];
          this.reservationsTotalItems.set(res.totalItems || 0);
        },
        error: () => {
          this.reservations = [];
          this.reservationsTotalItems.set(0);
        }
      });
  }

  onReservationsPageChange(page: number) {
    this.reservationsPage.set(page);
    this.loadReservationsTTL();
  }

  onReservationsPageSizeChange(size: number) {
    this.reservationsPageSize.set(size);
    this.reservationsPage.set(1);
    this.loadReservationsTTL();
  }

  applyReservationsFilters() {
    this.reservationsPage.set(1);
    this.loadReservationsTTL();
  }

  clearReservationsFilters() {
    this.reservationsFilterKey = null;
    this.reservationsFilterValue = null;
    this.applyReservationsFilters();
  }

  registerAdjustment() {
    if (this.adjustmentForm.invalid) {
      this.adjustmentForm.markAllAsTouched();
      return;
    }

    this.savingAdjustment.set(true);

    const inventoryAdjustment: AddInventoryAdjustment = {
      versionId: this.header.versionId,
      movementTypeId: this.adjustmentForm.value.movementTypeId ?? 0,
      quantity: this.adjustmentForm.value.quantity ?? 0,
      reason: this.adjustmentForm.value.reason ?? ''
    };

    this.inventorySvc.addInventoryAdjustment(inventoryAdjustment)
      .pipe(finalize(() => this.savingAdjustment.set(false)))
      .subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: 'Ajuste registrado',
            text: 'El inventario fue actualizado correctamente',
            timer: 1800,
            showConfirmButton: false
          });

          this.adjustmentForm.reset({
            movementTypeId: 1,
            quantity: null,
            reason: ''
          });
          this.loadKpis();
          this.movementsPage.set(1);
          this.loadMovements();
          this.loadReservationsTTL();
          this.changeData.emit();
        },
        error: (err) => {
          Swal.fire(
            'Error',
            err?.[0] ?? 'No se pudo registrar el ajuste',
            'error'
          );
        }
      });
  }

  release(r: any) {
    Swal.fire({
      title: '¿Liberar reserva?',
      text: 'Esto cancelará el link de pago y devolverá el stock.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Liberar'
    }).then(res => {
      if (res.isConfirmed) {
        this.inventorySvc.releaseReservation(r.reservationId)
          .subscribe({
            next: () => {
              Swal.fire({
                icon: 'success',
                title: 'Reserva liberada',
                timer: 1500,
                showConfirmButton: false
              });
              this.loadKpis();
              this.loadReservationsTTL();
              this.changeData.emit();
            },
            error: (err) => {
              Swal.fire(
                'Error',
                err?.[0] ?? 'No se pudo liberar la reserva',
                'error'
              );
            }
          });
      }
    });
  }


}